import { IProduct, IProductType, ISubscription } from "@dataone/lib";
import { Button, Progress, Slider } from "antd";
import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { ProductData } from "./Modal";

interface IncreaseModalProps {
  isSearchIncrease: boolean;
  isPromptChartUpdate: boolean;
  subscription: ISubscription;
  productsData: IProduct[];
  setShowPayment: (value: boolean) => void;
  setSearchData: (value: ProductData) => void;
  searchData: ProductData;
}

const IncreaseModal = ({
  isSearchIncrease,
  isPromptChartUpdate,
  subscription,
  productsData,
  setShowPayment,
  setSearchData,
  searchData,
}: IncreaseModalProps) => {
  const searchColors = {
    "0%": "#8D4CFE",
    "100%": "#4974F6",
  };
  const chatColors = {
    "0%": "rgba(129, 212, 254, 0.20)",
    "100%": "#16CCBF",
  };

  const promptChartUpdateColors = {
    "0%": "rgba(242, 211, 131, 0.20)",
    "100%": "#FFB800",
  };
  let searchPercent: any =
    (subscription.searchUsage * 100) / subscription.searchLimit;
  let chatPercent: any =
    (subscription.chatUsage * 100) / subscription.chatLimit;
  let promptChartUpdatePercent: any =
    (subscription.promptGridItemUpdateUsage * 100) /
    subscription.promptGridItemUpdateLimit;

  useEffect(() => {
    const searchMinValue = productsData?.find(
      (product) => product.type === IProductType.SEARCH
    )?.minimumQuantity;
    const chatMinValue = productsData?.find(
      (product) => product.type === IProductType.CHAT
    )?.minimumQuantity;
    const promptChartUpdateMinValue = productsData?.find(
      (product) => product.type === IProductType.PROMPT_CHART_UPDATE
    )?.minimumQuantity;

    const searchUnitAmount = productsData?.find(
      (product) => product.type === IProductType.SEARCH
    )?.unitAmount;
    const chatUnitAmount = productsData?.find(
      (product) => product.type === IProductType.CHAT
    )?.unitAmount;
    const promptChartUpdateUnitAmount = productsData?.find(
      (product) => product.type === IProductType.PROMPT_CHART_UPDATE
    )?.unitAmount;

    const searchMaxValue = subscription.searchLimit - subscription.searchUsage;
    const chatMaxValue = subscription.chatLimit - subscription.chatUsage;
    const promptChartUpdateMaxValue =
      subscription.promptGridItemUpdateLimit -
      subscription.promptGridItemUpdateUsage;

    setSearchData({
      searchMinValue: searchMinValue || 0,
      searchMaxValue: searchMaxValue || 0,
      searchUnitAmount: searchUnitAmount || 0,
      searchPrice: searchUnitAmount! * searchMinValue!,
      chatMinValue: chatMinValue || 0,
      chatMaxValue: chatMaxValue || 0,
      chatUnitAmount: chatUnitAmount || 0,
      chatPrice: chatUnitAmount! * searchMinValue!,
      promptChartUpdateMinValue: promptChartUpdateMinValue || 0,
      promptChartUpdateMaxValue: promptChartUpdateMaxValue || 0,
      promptChartUpdateUnitAmount: promptChartUpdateUnitAmount || 0,
      promptChartUpdatePrice:
        promptChartUpdateUnitAmount! * promptChartUpdateMinValue!,
      quantity: isSearchIncrease
        ? searchMinValue || 0
        : isPromptChartUpdate
        ? promptChartUpdateMinValue || 0
        : chatMinValue || 0,
    });
  }, [productsData]);

  const onChangeSliderValue = (value: number) => {
    isSearchIncrease
      ? setSearchData({
          ...searchData,
          searchPrice: value * searchData!.searchUnitAmount,
          quantity: value,
        })
      : isPromptChartUpdate
      ? setSearchData({
          ...searchData,
          promptChartUpdatePrice:
            value * searchData!.promptChartUpdateUnitAmount,
          quantity: value,
        })
      : setSearchData({
          ...searchData,
          chatPrice: value * searchData!.chatUnitAmount,
          quantity: value,
        });
  };

  const confirmIncrease = () => {
    setShowPayment(true);
  };
  return (
    <>
      <StyledHeader>
        <h2>
          {isSearchIncrease
            ? "Increase AI Search usage"
            : isPromptChartUpdate
            ? "Increase AI Prompt Chart Update usage"
            : "Increase AI Consultancy usage"}
        </h2>
      </StyledHeader>
      <StyledContent>
        <CurrentState>
          <h2>Your current Ai usage</h2>
          <Value
            $isSearchIncrease={isSearchIncrease}
            $isPromptChartUpdate={isPromptChartUpdate}
          >
            {isSearchIncrease
              ? subscription.searchUsage
              : isPromptChartUpdate
              ? subscription.promptGridItemUpdateUsage
              : subscription.chatUsage}
            <Limit
              $isSearchIncrease={isSearchIncrease}
              $isPromptChartUpdate={isPromptChartUpdate}
            >
              /
              {isSearchIncrease
                ? subscription.searchLimit
                : isPromptChartUpdate
                ? subscription.promptGridItemUpdateLimit
                : subscription.chatLimit}
            </Limit>
          </Value>
        </CurrentState>
        <StyledProgress>
          <Progress
            strokeColor={
              isSearchIncrease
                ? searchColors
                : isPromptChartUpdate
                ? promptChartUpdateColors
                : chatColors
            }
            type="dashboard"
            percent={
              isSearchIncrease
                ? searchPercent.toFixed(0)
                : isPromptChartUpdate
                ? promptChartUpdatePercent.toFixed(0)
                : chatPercent.toFixed(0)
            }
            format={(chat: number | undefined) => `${chat} %`}
          />
          <StyledText>
            {(searchPercent.toFixed(0) < 100 &&
              isSearchIncrease &&
              !isPromptChartUpdate) ||
            (chatPercent.toFixed(0) < 100 &&
              !isSearchIncrease &&
              !isPromptChartUpdate) ||
            (promptChartUpdatePercent.toFixed(0) < 100 &&
              isPromptChartUpdate &&
              !isSearchIncrease)
              ? `You may add app to ${
                  isSearchIncrease
                    ? subscription.searchLimit - subscription.searchUsage
                    : isPromptChartUpdate
                    ? subscription.promptGridItemUpdateLimit -
                      subscription.promptGridItemUpdateUsage
                    : subscription.chatLimit - subscription.chatUsage
                } usage`
              : "You have reached your limit"}
          </StyledText>
          <StyledSlider>
            <p>
              {isSearchIncrease
                ? searchData?.searchMaxValue
                : isPromptChartUpdate
                ? searchData?.promptChartUpdateMaxValue
                : searchData?.chatMaxValue}
            </p>
            <Slider
              defaultValue={
                isSearchIncrease
                  ? searchData?.searchMinValue
                  : isPromptChartUpdate
                  ? searchData?.promptChartUpdateMinValue
                  : searchData?.chatMinValue
              }
              min={
                isSearchIncrease
                  ? searchData?.searchMinValue
                  : isPromptChartUpdate
                  ? searchData?.promptChartUpdateMinValue
                  : searchData?.chatMinValue
              }
              // min={0}
              // max={12}
              onChange={onChangeSliderValue}
              max={
                isSearchIncrease
                  ? searchData?.searchMaxValue
                  : isPromptChartUpdate
                  ? searchData?.promptChartUpdateMaxValue
                  : searchData?.chatMaxValue
              }
            />
          </StyledSlider>
          <StyledText>
            {isSearchIncrease
              ? searchData?.searchPrice.toFixed(2)
              : isPromptChartUpdate
              ? searchData?.promptChartUpdatePrice.toFixed(2)
              : searchData?.chatPrice.toFixed(2)}
            $
          </StyledText>
          <Button className="confirm-btn" onClick={confirmIncrease}>
            Confirmer
          </Button>
        </StyledProgress>
      </StyledContent>
    </>
  );
};

export default IncreaseModal;
const StyledHeader = styled.div`
  margin-top: -10px;
  h2 {
    color: #4974f6;
    font-weight: 700;
    font-size: 25px;
    margin-top: 2px;
  }
`;
const StyledContent = styled.div``;
const CurrentState = styled.div`
  h2 {
    color: #040726;
    font-size: 16px;
    font-weight: 500;
    margin-top: 0px;
  }
`;
const Value = styled.div<{
  $isSearchIncrease: boolean;
  $isPromptChartUpdate: boolean;
}>`
  color: ${(p) =>
    p.$isSearchIncrease
      ? "#4974f6"
      : p.$isPromptChartUpdate
      ? "#FFB800"
      : "#16CCBF"};
  font-size: 18px;
  font-weight: 700;
`;
const Limit = styled.span<{
  $isSearchIncrease: boolean;
  $isPromptChartUpdate: boolean;
}>`
  color: ${(p) =>
    p.$isSearchIncrease
      ? "#4974f6"
      : p.$isPromptChartUpdate
      ? "#FFB800"
      : "#16CCBF"}
  font-size: 13px;
  font-weight: 700;
`;
const StyledProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  .ant-progress-text {
    color: #000 !important;
    font-weight: bold !important;
  }
  .ant-slider-horizontal {
    width: 300px;
    padding-block: 5px;
  }
  .ant-slider-horizontal .ant-slider-rail {
    height: 2px !important;
  }
  .ant-slider-horizontal .ant-slider-track {
    height: 2px !important;
  }
  .ant-slider .ant-slider-track {
    background-color: #4974f6 !important;
  }
  .ant-slider .ant-slider-rail {
    background-color: rgba(73, 116, 246, 0.5) !important;
  }
  .ant-slider .ant-slider-handle::after {
    box-shadow: 0 0 0 1px #000 !important;
  }
  .confirm-btn {
    width: 300px;
    margin-top: 20px;
    background-color: #4974f6;
    color: #fff;
    border: none;
    border-radius: 10px;
    height: 38px;
    &:hover {
      color: #fff !important;
      border: none;
    }
  }
`;
const StyledSlider = styled.div`
  position: relative;
  margin: 20px 0 0 0;
  p {
    position: absolute;
    right: 6px;
    bottom: 5px;
    font-size: 15px;
  }
`;
const StyledText = styled.p`
  font-size: 15px;
  margin: 0;
`;
